import { inject, Injectable } from '@angular/core';
import type { Contact } from '@hunting-nature/shared-core/interfaces/contact-response.interface';
import { switchMap, type Observable } from 'rxjs';

import { ContactsRestService } from '../../../../shared/services/contacts/contacts.rest.service';

import type { CameraContactsPermission } from './camera-contacts.types';

export interface CameraContactsFlowRequest {
  cameraId: string;
  contactName: string;
  contactEmail: string;
  permission: CameraContactsPermission;
}

@Injectable({
  providedIn: 'root',
})
/**
 * Orchestrates the camera contact flow (duplicate detection + sharing) so
 * effects can remain declarative and focused purely on action handling.
 */
export class CameraContactsFlowService {
  private readonly contactsService = inject(ContactsRestService);

  shareCameraContact$(request: CameraContactsFlowRequest): Observable<Contact> {
    return this.contactsService.fetchAllContacts$().pipe(
      switchMap(contacts => {
        const existingContact = this.findExistingContact(contacts, request.contactEmail);

        if (existingContact?.id) {
          const alreadyOnCamera = existingContact.sharedCameras.some(shared => shared.cameraId === request.cameraId);

          if (alreadyOnCamera) {
            return this.contactsService.updateCameraContactPermission$(existingContact.id, request.cameraId, request.permission);
          }

          return this.contactsService.shareCameraWithContact$(existingContact.id, {
            cameraId: request.cameraId,
            permission: request.permission,
          });
        }

        return this.contactsService
          .addContact$({
            alias: request.contactName,
            invitationEmail: request.contactEmail,
            email: request.contactEmail,
            accepted: false,
            acceptDate: undefined,
            sharedCameras: [],
          })
          .pipe(
            switchMap(contact =>
              this.contactsService.shareCameraWithContact$(contact.id, {
                cameraId: request.cameraId,
                permission: request.permission,
              })
            )
          );
      })
    );
  }

  private findExistingContact(contacts: Array<Contact>, email: string): Contact | undefined {
    const normalizedEmail = email.toLowerCase();

    return contacts.find(contact => {
      const contactEmail = contact.email?.toLowerCase();
      const invitationEmail = contact.invitationEmail?.toLowerCase();
      return contactEmail === normalizedEmail || invitationEmail === normalizedEmail;
    });
  }
}
